// Transform raw ActivityLog rows for a submission (as returned by
// GET /api/activity-logs/entity/:submissionId) into an ordered list of
// semantic journey events for the timeline UI.
//
// The backend records status transitions as UPDATE_SUBMISSION rows with a
// `details.changes` diff (see submissionController.updateSubmission). We
// unpack the diff here so the frontend never has to interpret raw log shape.
//
// Every emitted event is:
//   { at:        ISOString,
//     type:      'submitted' | 'status_change' | 'interview_scheduled' |
//                'interview_result' | 'reason' | 'note' | 'other',
//     label:     short human-readable line,
//     detail?:   longer sub-line (reason text, interview notes, etc),
//     from?:     previous status                          (status_change only)
//     to?:       new status                               (status_change only)
//     round?:    interview round number                   (interview_* only)
//     result?:   'Pass' | 'Fail' | 'Pending'              (interview_result only)
//     actor:     { name, role, impersonatedBy? } }
//
// Ordering: ascending by `at` (oldest first) so the timeline reads top-to-bottom.

import { fmtTimeRange } from './format';

const STATUS_LABEL = {
  Applied:     'Applied',
  Screening:   'Screening',
  Shortlisted: 'Shortlisted',
  Interview:   'Interview',
  Hold:        'On Hold',
  Dropped:     'Dropped',
  Rejected:    'Rejected',
  Hired:       'Hired',
};

const REASON_FIELDS = {
  rejectionReason: 'Rejection reason',
  holdReason:      'Hold reason',
  droppedReason:   'Dropped reason',
};

// Extract a single actor snapshot from a log row.
const actorOf = (log) => ({
  name: log.user?.name || 'Someone',
  role: log.user?.role || '',
  impersonatedBy: log.impersonatedBy?.name || null,
});

// Diff two interviewRounds arrays; emit an event per new round and per round
// whose `result` transitioned from Pending → Pass/Fail (or was changed).
const diffInterviewRounds = (before, after, at, actor) => {
  const events = [];
  const b = Array.isArray(before) ? before : [];
  const a = Array.isArray(after)  ? after  : [];

  const byRoundBefore = new Map(b.map(r => [r?.round, r]));
  for (const round of a) {
    if (!round || round.round == null) continue;
    const prior = byRoundBefore.get(round.round);
    // New round scheduled
    if (!prior) {
      const parts = [];
      if (round.date) parts.push(round.date);
      if (round.time) parts.push(fmtTimeRange(round.time, round.endTime));
      if (round.mode) parts.push(round.mode);
      events.push({
        at,
        type:   'interview_scheduled',
        label:  `Interview Round ${round.round} scheduled${round.title ? ` — ${round.title}` : ''}`,
        detail: parts.join(' · ') || (round.notes || ''),
        round:  round.round,
        actor,
      });
      continue;
    }
    // Result transitioned
    if (prior.result !== round.result && round.result && round.result !== 'Pending') {
      events.push({
        at,
        type:   'interview_result',
        label:  `Round ${round.round}: ${round.result}`,
        detail: round.resultDesc || '',
        round:  round.round,
        result: round.result,
        actor,
      });
    }
  }
  return events;
};

// Convert one ActivityLog row → 0..N events.
const eventsFromLog = (log) => {
  const at     = log.createdAt;
  const actor  = actorOf(log);
  const events = [];

  if (log.action === 'CREATE_SUBMISSION') {
    // The CREATE row records { candidateName, clientName }; the requirement
    // job-title is not persisted on the log. Compose a "to <client>" hint
    // when available so the event line has some context.
    const client = log.details?.clientName;
    events.push({
      at,
      type:   'submitted',
      label:  'Submitted',
      detail: client ? `to ${client}` : '',
      actor,
    });
    return events;
  }

  const changes = log.details?.changes || {};

  // Status transition
  if (changes.status && changes.status.after && changes.status.before !== changes.status.after) {
    events.push({
      at,
      type:   'status_change',
      label:  `${STATUS_LABEL[changes.status.before] || changes.status.before || 'Unknown'} → ${STATUS_LABEL[changes.status.after] || changes.status.after}`,
      from:   changes.status.before,
      to:     changes.status.after,
      actor,
    });
  }

  // Interview-round adds / result changes
  if (changes.interviewRounds) {
    events.push(...diffInterviewRounds(changes.interviewRounds.before, changes.interviewRounds.after, at, actor));
  }

  // Reason fields — capture the "why" text alongside the transition
  for (const [field, label] of Object.entries(REASON_FIELDS)) {
    if (changes[field] && changes[field].after) {
      events.push({
        at,
        type:   'reason',
        label,
        detail: changes[field].after,
        actor,
      });
    }
  }

  // Notes change — only surface when notes were populated (skip clearing)
  if (changes.notes && changes.notes.after) {
    events.push({
      at,
      type:   'note',
      label:  'Note added',
      detail: changes.notes.after,
      actor,
    });
  }

  // If nothing structured came out but this was still an UPDATE_SUBMISSION,
  // emit a generic "edited" bullet so no log row is silently dropped.
  if (events.length === 0 && log.action === 'UPDATE_SUBMISSION') {
    events.push({
      at,
      type:   'other',
      label:  'Submission edited',
      actor,
    });
  }

  return events;
};

// Public entry point.
//   logs         — array of ActivityLog rows for a submission (any order)
//   submittedAt  — optional fallback: the submission's own createdAt, used
//                  to seed the timeline when the CREATE_SUBMISSION log is
//                  missing (legacy rows from before activity logging).
export default function buildJourney(logs, submittedAt) {
  const rows = Array.isArray(logs) ? logs : [];
  const events = rows.flatMap(eventsFromLog);

  // Seed with a "Submitted" event if we didn't get a CREATE row from the log.
  const hasSubmitted = events.some(e => e.type === 'submitted');
  if (!hasSubmitted && submittedAt) {
    events.push({
      at:     submittedAt,
      type:   'submitted',
      label:  'Submitted',
      detail: 'History before this point was not recorded.',
      actor:  { name: '—', role: '', impersonatedBy: null },
    });
  }

  events.sort((a, b) => new Date(a.at) - new Date(b.at));
  return events;
}
